import React from 'react';
import TimerDisplay from './TimerDisplay';
import { useCountdownToMonthEndUTC } from '../../../hooks/useCountdownToMonthEndUTC';
import { streamMediaURL, userRoles } from '../../utility/ReUsableFunctions';
import axios from 'axios';
import { URL } from '../../../api/tklEnvironments';
import { useSelector } from 'react-redux';

interface Challenge {
  id: number;
  name: string;
  description: string;
  progress: number;
  total: number;
  points: number;
  is_monthly_repeatable: boolean;
  is_one_time: boolean;
  total_completions: number;
  monthly_completions: number;
  total_points_earned: number;
  monthly_points_earned: number;
  is_completed: boolean;
  sort_order?: number;
  action_url?: string;
  is_hidden?: boolean; // Hidden challenges only show in completed tab if completed
}

interface MonthlyReward {
  id: number;
  name: string;
  description: string;
  image_path: string;
  reward_type: string;
  placement: number;
}

interface UserStats {
  monthly_points: number;
  total_points: number;
  completion_percent: number;
  completed_challenges: number;
  total_challenges: number;
}

interface ChallengesResponse {
  challenges: Challenge[];
  monthly_reward: MonthlyReward[] | null;
  user_stats: UserStats | null;
  success: boolean;
  error?: string;
}

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5L15 12L8 19" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RewardSlider = ({ rewards }: { rewards: MonthlyReward[] }) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const nextReward = () => {
    setCurrentIndex((prev) => (prev + 1) % rewards.length);
  };

  const prevReward = () => {
    setCurrentIndex((prev) => (prev - 1 + rewards.length) % rewards.length);
  };

  const getImageUrl = (imagePath: string) => {
    if (!imagePath) return null;
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Otherwise, use streamMediaURL for relative paths
    return streamMediaURL(imagePath);
  };

  // Get background gradient based on placement
  const getBackgroundGradient = (placement: number) => {
    switch (placement) {
      case 1:
        // First place - Green gradient
        return 'linear-gradient(135deg, #18998E 0%, #4CAF50 50%, #66BB6A 100%)';
      case 2:
        // Second place - Orange gradient
        return 'linear-gradient(135deg, #FCB227 0%, #FCA311 50%, #FF8A00 100%)';
      case 3:
        // Third place - Blue gradient
        return 'linear-gradient(135deg, #2196F3 0%, #42A5F5 50%, #64B5F6 100%)';
      default:
        // Default - Platform primary gradient
        return 'linear-gradient(135deg, #18998E 0%, #20B2AA 50%, #48CAE4 100%)';
    }
  };

  // Get badge color based on placement
  const getBadgeColor = (placement: number) => {
    switch (placement) {
      case 1:
        return '#4CAF50'; // Green for 1st
      case 2:
        return '#FCB227'; // Orange for 2nd
      case 3:
        return '#2196F3'; // Blue for 3rd
      default:
        return '#18998E'; // Platform primary
    }
  };

  if (!rewards || rewards.length === 0) {
    return null;
  }

  const currentReward = rewards[currentIndex];

  return (
    <div style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: 24,
    }}>
      {/* Slider Container with Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        gap: 16,
      }}>
        {/* Left Arrow - only show if more than 1 reward */}
        {rewards.length > 1 ? (
          <button
            onClick={prevReward}
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : (
          <div style={{ width: 36, height: 36, flexShrink: 0 }} />
        )}

        {/* Reward Container */}
        <div style={{
          position: 'relative',
          width: '100%',
          maxWidth: 400,
          height: 200,
          borderRadius: 16,
          background: getBackgroundGradient(currentReward.placement),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          transition: 'background 0.3s ease',
        }}>
          {/* Reward Content */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px',
            height: '100%',
            width: '100%',
            gap: 16,
          }}>
            {/* Left side - Text Information */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              flex: 1,
              textAlign: 'left',
            }}>
              {/* Placement Badge */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '4px 12px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 700,
                color: getBadgeColor(currentReward.placement),
                marginBottom: 8,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                {currentReward.placement === 1 ? '1st Place' : 
                 currentReward.placement === 2 ? '2nd Place' :
                 currentReward.placement === 3 ? '3rd Place' :
                 `${currentReward.placement}th Place`}
              </div>

              {/* Reward Name */}
              <div style={{
                color: '#fff',
                fontWeight: 700,
                fontSize: 18,
                lineHeight: '22px',
                marginBottom: 4,
              }}>
                {currentReward.name}
              </div>

              {/* Reward Description */}
              {currentReward.description && (
                <div style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 500,
                  fontSize: 14,
                  lineHeight: '18px',
                }}>
                  {currentReward.description}
                </div>
              )}
            </div>

            {/* Right side - Reward Image */}
            {currentReward.image_path && (
              <div style={{
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                padding: '10px 10px',
                backgroundColor: '#fff',
                flexShrink: 0,
              }}>
                <img
                  src={getImageUrl(currentReward.image_path)}
                  alt={currentReward.name}
                  style={{
                    maxWidth: 80,
                    maxHeight: 80,
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Arrow - only show if more than 1 reward */}
        {rewards.length > 1 ? (
          <button
            onClick={nextReward}
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : (
          <div style={{ width: 36, height: 36, flexShrink: 0 }} />
        )}
      </div>

      {/* Dots Indicator - only show if more than 1 reward */}
      {rewards.length > 1 && (
        <div style={{
          display: 'flex',
          gap: 8,
          marginTop: 16,
        }}>
          {rewards.map((reward, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                border: 'none',
                background: index === currentIndex ? getBadgeColor(reward.placement) : '#E5E5E5',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ChallengeCard = ({ challenge, activeTab }) => {
  const isCompleted = challenge.is_completed;
  
        // Determine the text to show below the blue box
      const getBottomText = () => {
        if (activeTab === 'Completed') {
          // In Completed tab, show detailed completion info
          if (challenge.is_monthly_repeatable) {
            // For monthly repeatable actions, show monthly completions
            if (challenge.monthly_completions >= 1) {
              return `Done ${challenge.monthly_completions} time${challenge.monthly_completions === 1 ? '' : 's'} this month`;
            } else {
              return 'Done this month';
            }
          } else if (challenge.is_one_time) {
            // For one-time actions, just show "Completed"
            return 'Completed';
          } else {
            // For other actions, show total completions
            if (challenge.total_completions >= 1) {
              return `Completed ${challenge.total_completions} time${challenge.total_completions === 1 ? '' : 's'}`;
            } else {
              return 'Completed';
            }
          }
        } else {
          // In Available tab - check if it was completed before
          if (challenge.is_monthly_repeatable) {
            if (challenge.monthly_completions >= 1) {
              return 'Done';
            }
          } else if (challenge.is_one_time) {
            // For one-time actions
            if (challenge.total_completions >= 1) {
              return 'Completed';
            }
          } else {
            // For other actions (fallback)
            if (challenge.total_completions >= 1) {
              return 'Completed';
            }
          }
          return 'Available';
        }
      };

  const getPointsText = () => {
    if (activeTab === 'Completed') {
      // In Completed tab, show earned points
      if (challenge.is_monthly_repeatable) {
        return `Earned ${challenge.monthly_points_earned} Points`;
      } else {
        return `Earned ${challenge.total_points_earned} Points`;
      }
    } else {
      // In Challenges Available tab, show potential points
      return `Earn ${challenge.points} Points`;
    }
  };

  const handleChallengeClick = () => {
    console.log('Challenge clicked:', challenge.name, challenge.action_url);
    
    if (challenge.action_url) {
      // Check if it's an external URL or internal route
      if (challenge.action_url.startsWith('http://') || challenge.action_url.startsWith('https://')) {
        // External URL - open in new tab
        console.log('External URL, opening in new tab:', challenge.action_url);
        window.open(challenge.action_url, '_blank');
      } else {
        // Internal route - navigate within app
        // For posting-related challenges, add modal trigger parameter
        const postingChallenges = [
          'create_community_post',    // "Create a Post on the Community Page"
          'upload_image_resource',    // "Upload an Image Resource for your peers"
          'upload_resource_document', // "Upload a resources document for your colleagues"
          'share_educational_video'   // "Share an Educational Video"
        ];
        
        // Special handling for forum comment challenge
        if (challenge.name === 'forum_comment') {
          // Navigate to /the-loop and open the forums modal
          const modalParam = '?modal=forums';
          const finalUrl = challenge.action_url + modalParam;
          console.log('Forum comment challenge - navigating to:', finalUrl);
          window.location.href = finalUrl;
        } else if (challenge.name === 'create_cohort_team') {
          // Navigate to /the-loop and open the create group modal
          const modalParam = '?modal=create-group';
          const finalUrl = challenge.action_url + modalParam;
          console.log('Create cohort team challenge - navigating to:', finalUrl);
          window.location.href = finalUrl;
        } else if (postingChallenges.includes(challenge.name)) {
          // Map challenge names to post types
          const challengeToPostType = {
            'create_community_post': 'text',
            'upload_image_resource': 'image',
            'upload_resource_document': 'document',
            'share_educational_video': 'video'
          };
          
          const postType = challengeToPostType[challenge.name];
          const modalParam = `?modal=create-post&type=${postType}`;
          const finalUrl = challenge.action_url + modalParam;
          console.log('Posting challenge - navigating to:', finalUrl);
          window.location.href = finalUrl;
        } else {
          // Regular navigation for non-posting challenges
          console.log('Non-posting challenge - navigating to:', challenge.action_url);
          window.location.href = challenge.action_url;
        }
      }
    } else {
      console.log('No action_url for challenge:', challenge.name);
    }
  };
  
  return (
    <div 
      onClick={() => {
        console.log('Challenge div clicked!', challenge.name);
        handleChallengeClick();
      }}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 40,
        cursor: challenge.action_url ? 'pointer' : 'default',
      }}>
      {/* Blue background section - Description */}
      <div style={{
        width: '100%',
        minHeight: 48,
        background: '#bbdef4',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
      }}>
        <div style={{
          fontFamily: 'SF Pro Text',
          fontWeight: 600,
          fontSize: 16,
          lineHeight: '20px',
          color: '#000',
          flex: 1,
        }}>
          {challenge.description}
        </div>
        <ArrowIcon />
      </div>
      
      {/* Status and Points section - Below blue background */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'left',
        justifyContent: 'space-between',
        padding: '6px 20px 0 5px',
      }}>
        <span style={{
          fontFamily: 'SF Pro Text',
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '18px',
          color: '#000',
        }}>
          {getBottomText()}
        </span>
        
        <span style={{
          fontFamily: 'SF Pro Text',
          fontWeight: 600,
          fontSize: 14,
          lineHeight: '18px',
          color: '#000',
          textAlign: 'right',
        }}>
          {getPointsText()}
        </span>
      </div>
    </div>
  );
};

// Editable Challenge Card Component
const EditableChallengeCard = ({ 
  challenge, 
  isEditing, 
  editData, 
  setEditData, 
  onEditStart, 
  onEditSave, 
  onEditCancel, 
  saving, 
  isAdmin,
  activeTab
}: {
  challenge: Challenge;
  isEditing: boolean;
  editData: { description: string; action_url: string };
  setEditData: (data: { description: string; action_url: string }) => void;
  onEditStart: () => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  saving: boolean;
  isAdmin: boolean;
  activeTab: string;
}) => {
  const isCompleted = challenge.is_completed;
  
  const getBottomText = () => {
    if (activeTab === 'Completed') {
      // In Completed tab, show detailed completion info
      if (challenge.is_monthly_repeatable) {
        // For monthly repeatable actions, show monthly completions
        if (challenge.monthly_completions >= 1) {
          return `Done ${challenge.monthly_completions} time${challenge.monthly_completions === 1 ? '' : 's'} this month`;
        } else {
          return 'Done this month';
        }
      } else if (challenge.is_one_time) {
        // For one-time actions, just show "Completed"
        return 'Completed';
      } else {
        // For other actions, show total completions
        if (challenge.total_completions >= 1) {
          return `Completed ${challenge.total_completions} time${challenge.total_completions === 1 ? '' : 's'}`;
        } else {
          return 'Completed';
        }
      }
    } else {
      // In Available tab - check if it was completed before
      if (challenge.is_monthly_repeatable) {
        if (challenge.monthly_completions >= 1) {
          return 'Done';
        }
      } else if (challenge.is_one_time) {
        // For one-time actions
        if (challenge.total_completions >= 1) {
          return 'Completed';
        }
      } else {
        // For other actions (fallback)
        if (challenge.total_completions >= 1) {
          return 'Completed';
        }
      }
      return 'Available';
    }
  };

  const getPointsText = () => {
    if (activeTab === 'Completed') {
      // In Completed tab, show earned points
      if (challenge.is_monthly_repeatable) {
        return `Earned ${challenge.monthly_points_earned} Points`;
      } else {
        return `Earned ${challenge.total_points_earned} Points`;
      }
    } else {
      // In Challenges Available tab, show potential points
      return `Earn ${challenge.points} Points`;
    }
  };

  const handleChallengeClick = () => {
    if (isEditing) return; // Don't navigate when editing
    
    console.log('EditableChallengeCard clicked:', challenge.name, challenge.action_url);
    
    if (challenge.action_url) {
      // Check if it's an external URL or internal route
      if (challenge.action_url.startsWith('http://') || challenge.action_url.startsWith('https://')) {
        // External URL - open in new tab
        console.log('External URL, opening in new tab:', challenge.action_url);
        window.open(challenge.action_url, '_blank');
      } else {
        // Internal route - navigate within app
        // For posting-related challenges, add modal trigger parameter
        const postingChallenges = [
          'create_community_post',    // "Create a Post on the Community Page"
          'upload_image_resource',    // "Upload an Image Resource for your peers"
          'upload_resource_document', // "Upload a resources document for your colleagues"
          'share_educational_video'   // "Share an Educational Video"
        ];
        
        // Special handling for forum comment challenge
        if (challenge.name === 'forum_comment') {
          // Navigate to /the-loop and open the forums modal
          const modalParam = '?modal=forums';
          const finalUrl = challenge.action_url + modalParam;
          console.log('Forum comment challenge - navigating to:', finalUrl);
          window.location.href = finalUrl;
        } else if (challenge.name === 'create_cohort_team') {
          // Navigate to /the-loop and open the create group modal
          const modalParam = '?modal=create-group';
          const finalUrl = challenge.action_url + modalParam;
          console.log('Create cohort team challenge - navigating to:', finalUrl);
          window.location.href = finalUrl;
        } else if (postingChallenges.includes(challenge.name)) {
          // Map challenge names to post types
          const challengeToPostType = {
            'create_community_post': 'text',
            'upload_image_resource': 'image',
            'upload_resource_document': 'document',
            'share_educational_video': 'video'
          };
          
          const postType = challengeToPostType[challenge.name];
          const modalParam = `?modal=create-post&type=${postType}`;
          const finalUrl = challenge.action_url + modalParam;
          console.log('Posting challenge - navigating to:', finalUrl);
          window.location.href = finalUrl;
        } else {
          // Regular navigation for non-posting challenges
          console.log('Non-posting challenge - navigating to:', challenge.action_url);
          window.location.href = challenge.action_url;
        }
      }
    } else {
      console.log('No action_url for challenge:', challenge.name);
    }
  };
  
  return (
    <div 
      onClick={handleChallengeClick}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 40,
        cursor: challenge.action_url && !isEditing ? 'pointer' : 'default',
      }}>
      {/* Blue background section - Description */}
      <div style={{
        width: '100%',
        minHeight: 48,
        background: challenge.action_url ? '#bbdef4' : '#e8e8e8',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        position: 'relative',
        border: challenge.action_url ? '2px solid transparent' : '1px solid #ddd',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (challenge.action_url && !isEditing) {
          e.currentTarget.style.background = '#a8d3f0';
          e.currentTarget.style.border = '2px solid #18998E';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
        }
      }}
      onMouseLeave={(e) => {
        if (challenge.action_url && !isEditing) {
          e.currentTarget.style.background = '#bbdef4';
          e.currentTarget.style.border = '2px solid transparent';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}>
        {isEditing ? (
          <div 
            style={{ flex: 1, marginRight: '8px' }}
            onClick={(e) => e.stopPropagation()} // Prevent triggering challenge navigation
          >
            <input
              type="text"
              value={editData.description}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontFamily: 'SF Pro Text',
                fontSize: '16px',
                fontWeight: 600,
              }}
              placeholder="Challenge title"
            />
            <input
              type="text"
              value={editData.action_url}
              onChange={(e) => setEditData({ ...editData, action_url: e.target.value })}
              style={{
                width: '100%',
                padding: '6px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontFamily: 'SF Pro Text',
                fontSize: '14px',
                fontWeight: 400,
                marginTop: '8px',
              }}
              placeholder="Action URL (optional)"
            />
          </div>
        ) : (
          <div style={{
            fontFamily: 'SF Pro Text',
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '20px',
            color: '#000',
            flex: 1,
          }}>
            {challenge.description}
          </div>
        )}
        
        {/* Admin Edit Controls */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {isEditing ? (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering challenge navigation
                    onEditSave();
                  }}
                  disabled={saving}
                  style={{
                    background: '#18998E',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? '...' : '✓'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering challenge navigation
                    onEditCancel();
                  }}
                  disabled={saving}
                  style={{
                    background: '#f48980',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  ✕
                </button>
              </>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering challenge navigation
                  onEditStart();
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#666">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
              </button>
            )}
          </div>
        )}
        
        {!isEditing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {challenge.action_url && (
              <div style={{ 
                fontSize: '12px', 
                color: '#18998E', 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#18998E">
                  <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                </svg>
                Click to start
              </div>
            )}
            <ArrowIcon />
          </div>
        )}
      </div>
      
      {/* Status and Points section */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'left',
        justifyContent: 'space-between',
        padding: '6px 20px 0 5px',
      }}>
        <span style={{
          fontFamily: 'SF Pro Text',
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '18px',
          color: '#000',
        }}>
          {getBottomText()}
        </span>
        
        <span style={{
          fontFamily: 'SF Pro Text',
          fontWeight: 600,
          fontSize: 14,
          lineHeight: '18px',
          color: '#000',
          textAlign: 'right',
        }}>
          {getPointsText()}
        </span>
      </div>
    </div>
  );
};

const Challenges: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState('Challenges Available');
  const [challenges, setChallenges] = React.useState<Challenge[]>([]);
  const [monthlyRewards, setMonthlyRewards] = React.useState<MonthlyReward[]>([]);
  const [userStats, setUserStats] = React.useState<UserStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [draggedItem, setDraggedItem] = React.useState<Challenge | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);
  const [updating, setUpdating] = React.useState(false);
  const [editingChallenge, setEditingChallenge] = React.useState<number | null>(null);
  const [editData, setEditData] = React.useState({ description: '', action_url: '' });
  const [saving, setSaving] = React.useState(false);
  const timeLeft = useCountdownToMonthEndUTC();
  
  // Get user data for role checking
  const userData = useSelector((state: any) => state.users.userData);
  
  // Check if user has admin roles
  const isAdmin = userData?.roles?.includes(userRoles.SYSTEM_ADMIN) || 
                  userData?.roles?.includes(userRoles.TKL_COMMUNITY_MANAGER);

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get<ChallengesResponse>(
        `${URL}/rewards/challenges`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success && response.data.challenges) {
        // Sort challenges by sort_order if it exists
        const sortedChallenges = response.data.challenges.sort((a: Challenge, b: Challenge) => 
          (a.sort_order || 0) - (b.sort_order || 0)
        );
        setChallenges(sortedChallenges);
        setMonthlyRewards(response.data.monthly_reward || []);
        setUserStats(response.data.user_stats);
      } else {
        setError('Failed to load challenges');
      }
    } catch (err) {
      console.error('Error fetching challenges:', err);
      setError('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchChallenges();
  }, []);

  // Update challenge order
  const updateChallengeOrder = async (reorderedChallenges: Challenge[]) => {
    try {
      setUpdating(true);
      const challengesWithNewOrder = reorderedChallenges.map((challenge, index) => ({
        id: challenge.id,
        sort_order: index + 1
      }));

      await axios.post(`${URL}/admin/challenges/update-order`, {
        challenges: challengesWithNewOrder
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      // Update local state
      setChallenges(reorderedChallenges.map((challenge, index) => ({
        ...challenge,
        sort_order: index + 1
      })));

    } catch (error) {
      console.error('Error updating challenge order:', error);
      alert('Failed to update challenge order. Please try again.');
      // Refresh the list to restore original order
      fetchChallenges();
    } finally {
      setUpdating(false);
    }
  };

  // Edit challenge handlers
  const handleEditStart = (challenge: Challenge) => {
    setEditingChallenge(challenge.id);
    setEditData({
      description: challenge.description,
      action_url: challenge.action_url || ''
    });
  };

  const handleEditCancel = () => {
    setEditingChallenge(null);
    setEditData({ description: '', action_url: '' });
  };

  const handleEditSave = async (challengeId: number) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${URL}/admin/challenges/${challengeId}`, editData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Update local state
      setChallenges(challenges.map(challenge => 
        challenge.id === challengeId 
          ? { ...challenge, description: editData.description, action_url: editData.action_url }
          : challenge
      ));

      setEditingChallenge(null);
      setEditData({ description: '', action_url: '' });
      
    } catch (error) {
      console.error('Error updating challenge:', error);
      alert('Failed to update challenge title. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, challenge: Challenge) => {
    setDraggedItem(challenge);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're leaving the container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedItem) return;

    const dragIndex = filteredChallenges.findIndex(challenge => challenge.id === draggedItem.id);
    if (dragIndex === dropIndex) return;

    // Create new array with moved item
    const newChallenges = [...filteredChallenges];
    const draggedChallenge = newChallenges[dragIndex];
    newChallenges.splice(dragIndex, 1);
    newChallenges.splice(dropIndex, 0, draggedChallenge);

    // Update order in backend
    updateChallengeOrder(newChallenges);
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const filteredChallenges = activeTab === 'Completed' 
    ? challenges.filter(challenge => challenge.is_completed) // Show all completed challenges (including hidden ones)
    : challenges.filter(challenge => !challenge.is_hidden); // Hide hidden challenges from "Challenges Available" tab

  // Calculate completion percentage - exclude hidden challenges from progress calculation
  const visibleChallenges = challenges.filter(c => !c.is_hidden);
  const completedVisibleChallenges = visibleChallenges.filter(c => c.is_completed).length;
  const totalVisibleChallenges = visibleChallenges.length;
  const completionPercent = userStats?.completion_percent ?? 
    (totalVisibleChallenges > 0 ? Math.round((completedVisibleChallenges / totalVisibleChallenges) * 100) : 0);

  // Get description based on rewards
  const prizeDescription = monthlyRewards && monthlyRewards.length > 0
    ? `Complete all challenges to compete for amazing prizes including ${monthlyRewards.map(r => r.name).join(', ')}!`
    : "Complete all challenges to compete for amazing prizes!";

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ fontSize: 18, color: '#666' }}>Loading challenges...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ fontSize: 18, color: '#ff6b6b' }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%', margin: 'auto 0 auto', padding: '0 16px 220px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff' }}>
      {(updating || saving) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#fff',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <p>{saving ? 'Saving challenge title...' : 'Updating challenge order...'}</p>
          </div>
        </div>
      )}

      {/* Back Button */}
      <a 
        href="/rewards" 
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, marginTop: 28, cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
      >
        <span style={{ fontSize: 24, fontWeight: 700, marginRight: 12 }}>←</span>
        <span style={{ fontSize: 28, fontWeight: 700 }}>TKL Monthly Leaderboard</span>
      </a>

      {/* Page Title */}
      <h2 className="mb-4" style={{ textAlign: 'left', width: '100%' }}>TKL Monthly Challenges</h2>

      {/* Header Section */}
      <div style={{ width: '100%', maxWidth: 542, marginBottom: 32 }}>
        <div style={{
          fontFamily: 'SF Pro Text',
          fontWeight: 700,
          fontSize: 24,
          lineHeight: '32px',
          color: '#000',
          marginBottom: 8,
        }}>
          Complete Challenges & Improve Your Chance to WIN!
        </div>

        {/* Rewards Slider */}
        {monthlyRewards && monthlyRewards.length > 0 && (
          <RewardSlider rewards={monthlyRewards} />
        )}

        <div style={{
          fontFamily: 'SF Pro Text',
          fontWeight: 400,
          fontSize: 16,
          lineHeight: '24px',
          color: '#666',
          marginBottom: 24,
        }}>
          {prizeDescription}
        </div>
        
        {/* Timer and Progress Section */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#fff',
          padding: '24px',
          borderRadius: 16,
          border: '1px solid #E5E5E5',
          marginBottom: 24,
        }}>
          {/* Timer */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{
              fontFamily: 'SF Pro Text',
              fontWeight: 700,
              fontStyle: 'italic',
              fontSize: 20,
              color: '#000',
            }}>
              Time Remaining This Month
            </div>
            <TimerDisplay values={timeLeft} labels={['days', 'hours', 'minutes']} />
            <div style={{ 
              marginTop: 8, 
              textAlign: 'center',
              fontSize: 12,
              color: '#737373',
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#737373' }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>All times shown in UTC</span>
            </div>
          </div>
          
          {/* Progress Circle */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 100, height: 100, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width={100} height={100}>
                <defs>
                  <linearGradient id="greenGradient" x1="0" y1="100" x2="100" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="18.24%" stopColor="#85CD01" />
                    <stop offset="77.95%" stopColor="#A1F801" />
                  </linearGradient>
                  <linearGradient id="yellowGradient" x1="0" y1="100" x2="100" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="18.24%" stopColor="#FFD600" />
                    <stop offset="77.95%" stopColor="#FFF176" />
                  </linearGradient>
                </defs>
                <circle cx={50} cy={50} r={40} fill="none" stroke="#ECECEC" strokeWidth={12} />
                <circle 
                  cx={50} 
                  cy={50} 
                  r={40} 
                  fill="none" 
                  stroke={completionPercent === 100 ? "url(#greenGradient)" : "url(#yellowGradient)"} 
                  strokeWidth={12} 
                  strokeDasharray={2 * Math.PI * 40} 
                  strokeDashoffset={2 * Math.PI * 40 * (1 - completionPercent / 100)} 
                  strokeLinecap="round" 
                  transform="rotate(-90 50 50)" 
                />
                <text 
                  x="50%" 
                  y="50%" 
                  textAnchor="middle" 
                  dominantBaseline="central" 
                  fontFamily="SF Pro Text, Arial, sans-serif" 
                  fontWeight="700" 
                  fontSize="22" 
                  fill="#000" 
                  letterSpacing="0.875px"
                >
                  {completionPercent}%
                </text>
              </svg>
            </div>
            <div style={{
              fontFamily: 'SF Pro Text',
              fontWeight: 600,
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
            }}>
              Challenges<br />Completed
            </div>
          </div>
        </div>
        
        {/* Monthly Points Display */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #2bb9ad 0%, #1a8a7f 50%, #0d5a52 100%)',
          padding: '20px 24px',
          borderRadius: 16,
          marginBottom: 24,
          boxShadow: '0 4px 12px rgba(43, 185, 173, 0.15)',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{
              fontFamily: 'SF Pro Text',
              fontWeight: 600,
              fontSize: 14,
              color: 'rgba(255, 255, 255, 0.9)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              This Month's Points
            </div>
            <div style={{
              fontFamily: 'SF Pro Text',
              fontWeight: 700,
              fontSize: 28,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <span>🎯</span>
              {userStats?.monthly_points || 0} Points
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <div style={{
              fontFamily: 'SF Pro Text',
              fontWeight: 600,
              fontSize: 14,
              color: 'rgba(255, 255, 255, 0.9)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Total Points
            </div>
            <div style={{
              fontFamily: 'SF Pro Text',
              fontWeight: 700,
              fontSize: 20,
              color: 'rgba(255, 255, 255, 0.8)',
            }}>
              {userStats?.total_points || 0} Points
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ width: '100%', maxWidth: 438, height: 56, display: 'flex', flexDirection: 'row', background: '#fff', border: '1px solid #D8D8D8', borderRadius: 12, marginBottom: 32 }}>
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            background: activeTab === 'Challenges Available' ? '#18998E' : 'transparent', 
            color: activeTab === 'Challenges Available' ? '#fff' : '#29233B', 
            borderRadius: 12, 
            width: '50%', 
            height: 40, 
            margin: 8, 
            fontWeight: 700, 
            fontSize: 14, 
            cursor: 'pointer' 
          }}
          onClick={() => setActiveTab('Challenges Available')}
        >
          Challenges Available
        </div>
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            background: activeTab === 'Completed' ? '#18998E' : 'transparent', 
            color: activeTab === 'Completed' ? '#fff' : '#29233B', 
            borderRadius: 12, 
            width: '50%', 
            height: 40, 
            margin: 8, 
            fontWeight: 700, 
            fontSize: 14, 
            cursor: 'pointer' 
          }}
          onClick={() => setActiveTab('Completed')}
        >
          Completed
        </div>
      </div>

      {/* Admin Instructions */}
      {isAdmin && activeTab === 'Challenges Available' && filteredChallenges.length > 0 && (
        <div style={{
          background: '#e3f2fd',
          border: '1px solid #1976d2',
          borderRadius: '8px',
          padding: '0.75rem',
          marginBottom: '1rem',
          fontSize: '0.875rem',
          color: '#1976d2',
          width: '100%',
          maxWidth: 438,
        }}>
          💡 <strong>Admin:</strong> Drag and drop challenge items to reorder them. Click the edit button to edit challenge titles. Click anywhere on the challenge card to test the action. Changes are saved automatically.
        </div>
      )}

      {/* List of challenges */}
      <div style={{ width: '100%', maxWidth: 438, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        {filteredChallenges.map((challenge, index) => (
          isAdmin && activeTab === 'Challenges Available' ? (
            <div
              key={challenge.id}
              draggable={editingChallenge !== challenge.id} // Disable drag when editing
              onDragStart={(e) => handleDragStart(e, challenge)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                width: '100%',
                border: dragOverIndex === index ? '2px dashed #18998E' : 'none',
                borderRadius: '8px',
                cursor: editingChallenge === challenge.id ? 'default' : 'move',
                opacity: draggedItem && draggedItem.id === challenge.id ? 0.5 : 1,
                transform: draggedItem && draggedItem.id === challenge.id ? 'rotate(2deg)' : 'none',
                transition: 'all 0.2s ease',
                boxShadow: dragOverIndex === index ? '0 4px 8px rgba(24, 153, 142, 0.2)' : 'none',
                position: 'relative',
              }}
            >
              {/* Drag Handle for Admins - hide when editing */}
              {editingChallenge !== challenge.id && (
                <div 
                  onClick={(e) => e.stopPropagation()} // Prevent triggering challenge navigation
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#666',
                    fontSize: '1.2rem',
                    cursor: 'move',
                    userSelect: 'none',
                    zIndex: 10,
                    background: 'rgba(255,255,255,0.9)',
                    padding: '4px',
                    borderRadius: '4px',
                  }}>
                  ⋮⋮
                </div>
              )}
              <div style={{ marginLeft: editingChallenge === challenge.id ? '0px' : '40px' }}>
                <EditableChallengeCard
                  challenge={challenge}
                  isEditing={editingChallenge === challenge.id}
                  editData={editData}
                  setEditData={setEditData}
                  onEditStart={() => handleEditStart(challenge)}
                  onEditSave={() => handleEditSave(challenge.id)}
                  onEditCancel={handleEditCancel}
                  saving={saving}
                  isAdmin={isAdmin}
                  activeTab={activeTab}
                />
              </div>
            </div>
          ) : (
            <EditableChallengeCard
              key={challenge.id}
              challenge={challenge}
              isEditing={false}
              editData={editData}
              setEditData={setEditData}
              onEditStart={() => handleEditStart(challenge)}
              onEditSave={() => handleEditSave(challenge.id)}
              onEditCancel={handleEditCancel}
              saving={saving}
              isAdmin={isAdmin}
              activeTab={activeTab}
            />
          )
        ))}
      </div>
    </div>
  );
};

export default Challenges; 